/**
 * Description rendering — Specification §4.11.
 *
 * Description strings (bare-string body members) are interpreted as a
 * restricted markdown dialect with two archlang extensions:
 *
 *   - `[[#id]]` and `[[Name]]` — cross-references to other declarations
 *   - `@path` (field) / `@@path` (aspect) — interpolation of the owning node's
 *     value on the axis named by the sigil (§5.1); `[[ref]]@path` /
 *     `[[ref]]@@path` read a foreign node's field / aspect
 *
 * This module resolves the archlang extensions and emits a markdown
 * string ready for downstream renderers (LSP hover, web markdown
 * renderer). It does **not** parse markdown itself; the subset defined
 * in §4.11.1 is the consumer's contract.
 *
 * Refs are emitted as `archlang://` URIs:
 *
 *   - `[[#ord001]]`  →  `[Orders](archlang://id/ord001)`
 *   - `[[Orders]]`   →  `[Orders](archlang://name/Orders)`
 *
 * Issues (unresolved/ambiguous refs, missing aspects) are collected
 * alongside the rendered markdown so callers can emit diagnostics.
 */

import type { AspectEntry, FieldDecl, QualifiedName, Value } from "./types.js";
import type {
  ResolvedSurface,
  ResolvedInterface,
  ResolvedModel,
  ResolvedModule,
} from "./resolver.js";
import { decorationLookup } from "./decorations.js";
import type { DecorationInstance, DecorationRegistry } from "./decorations.js";
import { splitCodeRegions, scanRefs, classifyRef } from "./markdown/refs.js";
import { slugify, type MarkdownDocument } from "./markdown/document.js";
import { exprText } from "./selector-ast.js";
import { renderBlockWithIssues } from "@archlang/calc";

// =============================================================================
// Types
// =============================================================================

export interface DescriptionDeclRef {
  /** Stable id (no `#` prefix), if the target has one. */
  readonly id?: string;
  /** Short display name. */
  readonly name: string;
  /** Fully-qualified dot-path name. */
  readonly qualifiedName: string;
  /** Kind name (microservice, command, etc.). Informational. */
  readonly typeName?: string;
  /** Declaring space (v0.5). Undefined = package root. Drives nearest-first
   *  disambiguation of `[[Name]]` refs across spaces. */
  readonly space?: QualifiedName;
}

export interface DescriptionContext {
  /** Resolve a stable id (e.g. `"ord001"`). */
  resolveId(id: string): DescriptionDeclRef | undefined;
  /**
   * Resolve a name (e.g. `"Orders"` or `"Personal.Banking.Payments"`).
   * Returns all matches across the model. Caller decides ambiguity
   * handling per §4.11.2 (>1 match → diagnostic).
   */
  resolveName(name: string): readonly DescriptionDeclRef[];
  /**
   * Effective aspect value at the given path on the owning node,
   * including aspects inherited via the type chain (§4.11.3). Returns
   * `undefined` if the aspect is not set. Backs the `@@path` getter.
   */
  getAspect(path: readonly string[]): Value | undefined;
  /**
   * Effective FIELD value at the given path on the owning node (§5.1). Backs the
   * `@path` getter (fields are the value axis; `@@` is the aspect axis). Returns
   * `undefined` if the field is unset. Optional: when absent, `@path` renders as
   * `<missing:…>` (e.g. a `.md` document with no owning node).
   */
  getField?(path: readonly string[]): Value | undefined;
  /**
   * Read an aspect off ANOTHER node — the foreign getter `[[X]]@@team`
   * (§4.10). `target` is the resolved ref; returns its effective (cascaded)
   * aspect value, or `undefined` if unset. Optional: when absent, a foreign
   * getter renders as an unresolved ref.
   */
  getForeignAspect?(target: DescriptionDeclRef, path: readonly string[]): Value | undefined;
  /**
   * Read a FIELD off ANOTHER node — the foreign getter `[[X]]@field` (§5.1).
   * Returns its effective (cascaded) field value, or `undefined` if unset.
   * Optional: when absent, a foreign field getter renders as an unresolved ref.
   */
  getForeignField?(target: DescriptionDeclRef, path: readonly string[]): Value | undefined;
  /**
   * Whether an aspect `[[@key]]` / `[[@key:value]]` is present in the model.
   * Optional: when absent, aspect refs render without resolution checking.
   */
  resolveAspect?(key: string, value: string | undefined): boolean;
  /**
   * Section slugs of a document `[[Doc#section]]`, or `undefined` if there is
   * no such document. Optional: when absent, doc-section refs render without
   * resolution checking.
   */
  resolveDocument?(name: string): readonly string[] | undefined;
  /**
   * Space of the declaration that owns this description (v0.5). When set, an
   * ambiguous `[[Name]]` is narrowed nearest-first: candidates in this space,
   * else package-root candidates, else all. Undefined → no narrowing.
   */
  readonly referrerSpace?: QualifiedName;
}

export type DescriptionIssueKind =
  | "unresolved-ref"
  | "ambiguous-ref"
  | "missing-aspect"
  /** An `@path` field getter whose field is unset on the (owning or referenced)
   *  node (§5.1). */
  | "missing-field"
  /** A bare `@path`/`@@path` interpolation used in a `.md` document, where there
   *  is no owning node (§4.15). Only `[[ref]]`-prefixed foreign getters work there. */
  | "bare-interpolation"
  /** A broken calc statement/query inside a `~{ … }` region (§4.10.5). */
  | "calc-error";

export interface DescriptionIssue {
  readonly kind: DescriptionIssueKind;
  /** Raw text fragment that triggered the issue (e.g. `[[Orders]]`). */
  readonly text: string;
  /** Byte offset into the joined description string. */
  readonly offset: number;
  readonly length: number;
  /** Candidates returned for `ambiguous-ref`. */
  readonly candidates?: readonly DescriptionDeclRef[];
  /** Human-readable detail for `calc-error` (the calculator's message). */
  readonly message?: string;
}

export interface RenderedDescription {
  /** Markdown with refs resolved to links and aspects interpolated. */
  readonly markdown: string;
  readonly issues: readonly DescriptionIssue[];
}

// =============================================================================
// Public entry point
// =============================================================================

/**
 * Resolve archlang extensions (`[[…]]` refs, `@aspect` interpolation) in
 * a description string. Returns markdown for downstream rendering plus
 * a list of issues for diagnostic emission.
 *
 * The result preserves all markdown markup; the subset defined in
 * §4.11.1 is enforced by the downstream markdown renderer. Code
 * regions (fenced blocks and inline `` `code` ``) are left untouched —
 * extensions inside code are literal text.
 */
export function renderDescription(
  description: string,
  ctx: DescriptionContext
): RenderedDescription {
  const issues: DescriptionIssue[] = [];
  const segments = splitCodeRegions(description);
  let out = "";
  let cursor = 0;

  for (const seg of segments) {
    if (seg.kind === "code") {
      out += seg.text;
      cursor += seg.text.length;
      continue;
    }
    if (seg.kind === "calc") {
      const r = renderCalcRegion(seg.text, cursor, seg.precise);
      out += r.markdown;
      issues.push(...r.issues);
      cursor += seg.text.length;
      continue;
    }
    const { rendered, segIssues } = renderTextSegment(seg.text, cursor, ctx, true);
    out += rendered;
    issues.push(...segIssues);
    cursor += seg.text.length;
  }

  return { markdown: out, issues };
}

/**
 * Render a `~{ … }` calc region (§4.10) to plain markdown — never code-styled.
 * Multi-line napkins keep their line breaks via markdown hard breaks; a single
 * inline expression renders to just its value. The calculator is lenient, so a
 * malformed line is left as written rather than erroring the whole description.
 */
function renderCalcRegion(
  raw: string,
  baseOffset: number,
  precise = false
): { markdown: string; issues: DescriptionIssue[] } {
  const inner = raw.slice(2, -1); // strip `~{`/`={` and `}`
  let result: { text: string; issues: { message: string; offset: number; length: number }[] };
  try {
    result = renderBlockWithIssues(inner, precise);
  } catch {
    return { markdown: raw, issues: [] }; // leave verbatim if the calculator chokes
  }
  const issues: DescriptionIssue[] = result.issues.map((ci) => ({
    kind: "calc-error" as const,
    text: inner.slice(ci.offset, ci.offset + ci.length).trim(),
    offset: baseOffset + 2 + ci.offset, // +2 for the `~{`
    length: ci.length,
    message: ci.message,
  }));
  let md = result.text;
  if (md.includes("\n")) md = md.replace(/^\n+|\n+$/g, "").split("\n").join("  \n");
  else md = md.trim(); // single line: drop the `~{ … }` padding spaces
  return { markdown: md, issues };
}

/**
 * Render a standalone `.md` document body (§4.15). Same flavored-markdown
 * grammar as descriptions, but there is no owning node, so a bare `@path`
 * interpolation is an error — only the `[[ref]]@path` foreign getter reaches
 * a value. The raw body otherwise passes through verbatim.
 */
export function renderMarkdownDocument(
  body: string,
  ctx: DescriptionContext
): RenderedDescription {
  const issues: DescriptionIssue[] = [];
  const segments = splitCodeRegions(body);
  let out = "";
  let cursor = 0;
  for (const seg of segments) {
    if (seg.kind === "code") {
      out += seg.text;
      cursor += seg.text.length;
      continue;
    }
    if (seg.kind === "calc") {
      const r = renderCalcRegion(seg.text, cursor, seg.precise);
      out += r.markdown;
      issues.push(...r.issues);
      cursor += seg.text.length;
      continue;
    }
    const { rendered, segIssues } = renderTextSegment(seg.text, cursor, ctx, false);
    out += rendered;
    issues.push(...segIssues);
    cursor += seg.text.length;
  }
  return { markdown: out, issues };
}

// =============================================================================
// Text-segment rendering
// =============================================================================

/**
 * Render one already-code-stripped text segment, resolving `[[ … ]]` refs to
 * `archlang://` links and interpolating bare `@aspectPath` against the owning
 * node. Drives off the shared `scanRefs` tokenizer (§4.10) so descriptions and
 * `.md` documents share one grammar.
 */
function renderTextSegment(
  text: string,
  baseOffset: number,
  ctx: DescriptionContext,
  ownerContext: boolean
): { rendered: string; segIssues: DescriptionIssue[] } {
  const segIssues: DescriptionIssue[] = [];
  let out = "";
  const toks = scanRefs(text);

  const resolveElement = (inner: string): DescriptionDeclRef | undefined => {
    if (inner.startsWith("#")) return ctx.resolveId(inner.slice(1).trim());
    const cands = narrowBySpace(ctx.resolveName(inner.trim()), ctx.referrerSpace);
    return cands.length === 1 ? cands[0] : undefined;
  };

  for (let i = 0; i < toks.length; i++) {
    const tok = toks[i]!;
    if (tok.kind === "text") {
      out += tok.text;
      continue;
    }

    if (tok.kind === "ref") {
      const raw = tok.raw;
      const offset = baseOffset + tok.index;
      const ref = classifyRef(tok.inner);

      // Foreign getter `[[X]]@path` (field) / `[[X]]@@path` (aspect): a ref
      // immediately followed by a getter reads that value off the REFERENCED
      // node (§4.10 / §5.1), not the owner. The sigil picks the axis.
      const next = toks[i + 1];
      const adjacent =
        next !== undefined && next.kind === "getter" && next.index === tok.index + raw.length;
      if (adjacent && (ref.kind === "id" || ref.kind === "name")) {
        const getter = next as Extract<typeof next, { kind: "getter" }>;
        const isAspect = getter.sigil === "@@";
        const target = resolveElement(tok.inner);
        const read = isAspect ? ctx.getForeignAspect : ctx.getForeignField;
        const value = target && read ? read(target, getter.path.split(".")) : undefined;
        if (value !== undefined) {
          out += valueToString(value);
        } else {
          out += `<missing:${tok.inner}${getter.raw}>`;
          segIssues.push({
            kind: target ? (isAspect ? "missing-aspect" : "missing-field") : "unresolved-ref",
            text: `${raw}${getter.raw}`,
            offset,
            length: raw.length + getter.raw.length,
          });
        }
        i++; // consume the getter token
        continue;
      }

      switch (ref.kind) {
        case "id": {
          const target = ctx.resolveId(ref.id);
          if (target) out += refLink(target);
          else {
            out += unresolvedMarker(raw);
            segIssues.push({ kind: "unresolved-ref", text: raw, offset, length: raw.length });
          }
          break;
        }
        case "name": {
          const candidates = narrowBySpace(ctx.resolveName(ref.name), ctx.referrerSpace);
          if (candidates.length === 1) out += refLink(candidates[0]!);
          else if (candidates.length > 1) {
            out += ambiguousMarker(raw);
            segIssues.push({ kind: "ambiguous-ref", text: raw, offset, length: raw.length, candidates });
          } else if (ctx.resolveDocument && ctx.resolveDocument(ref.name) !== undefined) {
            // No element by that name, but a `.md` document has it — a bare
            // `[[Doc]]` whole-document link (the `[[Doc#section]]` form targets
            // a section). Element names win over documents when both exist.
            out += docLink(ref.name);
          } else {
            out += unresolvedMarker(raw);
            segIssues.push({ kind: "unresolved-ref", text: raw, offset, length: raw.length });
          }
          break;
        }
        case "aspect": {
          out += aspectLink(ref.key, ref.value);
          if (ctx.resolveAspect && !ctx.resolveAspect(ref.key, ref.value)) {
            segIssues.push({ kind: "unresolved-ref", text: raw, offset, length: raw.length });
          }
          break;
        }
        case "section": {
          const slug = slugify(ref.section);
          out += docSectionLink(ref.doc, ref.section, slug);
          if (ctx.resolveDocument) {
            const slugs = ctx.resolveDocument(ref.doc);
            if (slugs === undefined || !slugs.includes(slug)) {
              segIssues.push({ kind: "unresolved-ref", text: raw, offset, length: raw.length });
            }
          }
          break;
        }
      }
      continue;
    }

    // Bare getter `@path` (field) / `@@path` (aspect).
    const path = tok.path;
    const isAspect = tok.sigil === "@@";
    if (!ownerContext) {
      out += `<bare-interpolation:${path}>`;
      segIssues.push({
        kind: "bare-interpolation",
        text: tok.raw,
        offset: baseOffset + tok.index,
        length: tok.raw.length,
      });
      continue;
    }
    const value = isAspect
      ? ctx.getAspect(path.split("."))
      : ctx.getField?.(path.split("."));
    if (value !== undefined) {
      out += valueToString(value);
    } else {
      out += `<missing:${path}>`;
      segIssues.push({
        kind: isAspect ? "missing-aspect" : "missing-field",
        text: tok.raw,
        offset: baseOffset + tok.index,
        length: tok.raw.length,
      });
    }
  }

  return { rendered: out, segIssues };
}

function aspectLink(key: string, value: string | undefined): string {
  const suffix = value !== undefined ? `:${value}` : "";
  const uri = `archlang://aspect/${encodeURIComponent(key)}${value !== undefined ? `:${encodeURIComponent(value)}` : ""}`;
  return `[@@${key}${suffix}](${uri})`;
}

function docSectionLink(doc: string, section: string, slug: string): string {
  return `[${doc}#${section}](archlang://doc/${encodeURIComponent(doc)}#${slug})`;
}

function docLink(doc: string): string {
  return `[${doc}](archlang://doc/${encodeURIComponent(doc)})`;
}

function refLink(target: DescriptionDeclRef): string {
  const uri = target.id
    ? `archlang://id/${target.id}`
    : `archlang://name/${encodeURIComponent(target.qualifiedName)}`;
  return `[${target.name}](${uri})`;
}

function unresolvedMarker(raw: string): string {
  return `<unresolved:${raw.slice(2, -2)}>`;
}

function sameSpace(
  a: QualifiedName | undefined,
  b: QualifiedName | undefined
): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return a.length === b.length && a.every((s, i) => s === b[i]);
}

/**
 * Narrow `[[Name]]` candidates nearest-first by the referrer's space (v0.5):
 * candidates in the same space, else package-root candidates, else all. No
 * referrer space (or a single candidate) → unchanged.
 */
function narrowBySpace(
  candidates: readonly DescriptionDeclRef[],
  referrerSpace: QualifiedName | undefined
): readonly DescriptionDeclRef[] {
  if (!referrerSpace || candidates.length <= 1) return candidates;
  const same = candidates.filter((c) => sameSpace(c.space, referrerSpace));
  if (same.length >= 1) return same;
  const root = candidates.filter((c) => c.space === undefined);
  if (root.length >= 1) return root;
  return candidates;
}

function ambiguousMarker(raw: string): string {
  return `<ambiguous:${raw.slice(2, -2)}>`;
}

// =============================================================================
// Index — fast lookup over a ResolvedModel
// =============================================================================

/**
 * Pre-built index over a resolved model, exposing the `resolveId` and
 * `resolveName` halves of `DescriptionContext`. Build once per
 * model snapshot, reuse across all description renders.
 */
export interface DescriptionIndex {
  resolveId(id: string): DescriptionDeclRef | undefined;
  resolveName(name: string): readonly DescriptionDeclRef[];
  /** Effective (cascaded) aspect accessor for a node, keyed by qualified name —
   *  backs the foreign aspect getter `[[X]]@@team`. */
  aspectsFor(qualifiedName: string): ((path: readonly string[]) => Value | undefined) | undefined;
  /** Effective (cascaded) field accessor for a node, keyed by qualified name —
   *  backs the foreign field getter `[[X]]@field`. */
  fieldsFor(qualifiedName: string): ((path: readonly string[]) => Value | undefined) | undefined;
  /** Whether an aspect (key[:value]) — a string-valued aspect — exists anywhere. */
  resolveAspect(key: string, value: string | undefined): boolean;
  /** Section slugs of a document, or undefined if there is no such document. */
  resolveDocument(name: string): readonly string[] | undefined;
  /** The model's decoration vocabulary, so a caller building a LOCAL `@` axis
   *  (`nodeValueLookup`) resolves the same names the index's foreign getters
   *  do. Carried here because every description-rendering site already threads
   *  the index and none of them thread the model.
   *
   *  REQUIRED, though it may be undefined: as an optional property a second
   *  implementation of this interface simply omitted it, and the axis lost its
   *  primary-field sugar with nothing to say so. */
  readonly decorations: DecorationRegistry | undefined;
}

/**
 * Build an index over all named declarations in a resolved model:
 * modules (recursively), surfaces, interfaces, types, processes, views.
 * Stable IDs are only present on modules, types, processes, and views
 * (per Spec §5.1); surfaces and interfaces are indexed by name only.
 */
/**
 * Per-node cascaded aspect accessors (foreign getter `[[X]]@@team`) + field
 * accessors (foreign getter `[[X]]@field`) + the set of aspects (string-valued
 * aspects, `key` and `key:value`). Shared by the cold description index and the
 * incremental compiler so both resolve foreign getters / aspect refs identically
 * (cold == delta).
 */
export function collectDocAux(model: ResolvedModel): {
  aspectsByQName: Map<string, (path: readonly string[]) => Value | undefined>;
  fieldsByQName: Map<string, (path: readonly string[]) => Value | undefined>;
  aspectSet: Set<string>;
} {
  const aspectsByQName = new Map<string, (path: readonly string[]) => Value | undefined>();
  const fieldsByQName = new Map<string, (path: readonly string[]) => Value | undefined>();
  const aspectSet = new Set<string>();
  // The `@` axis of a node is its decorations then its fields (§4.3.1), so the
  // FOREIGN getter `[[X]]@planned.from` reads the same vocabulary as the local
  // one — otherwise a decoration would resolve on the node that carries it and
  // vanish when read across a reference.
  const reg = model.decorationRegistry;
  const collect = (
    qname: string,
    node: { aspects: readonly AspectEntry[]; fields: readonly FieldDecl[] },
    decorations?: readonly DecorationInstance[]
  ): void => {
    aspectsByQName.set(qname, aspectLookup(node.aspects));
    fieldsByQName.set(qname, nodeValueLookup(node.fields, decorations, reg));
    for (const l of node.aspects) {
      if (l.drop || l.value === undefined || l.value.kind !== "string") continue;
      const key = l.path.join(".");
      aspectSet.add(key);
      aspectSet.add(`${key}:${l.value.text}`);
    }
  };
  const walkSurface = (s: ResolvedSurface, parent: string): void => {
    const q = `${parent}.${s.name}`;
    collect(q, s.body, s.decl.decorations);
    for (const i of s.interfaces) collect(`${q}.${i.name}`, i.body, i.decl.decorations);
    for (const sub of s.surfaces) walkSurface(sub, q);
  };
  const walk = (mods: readonly ResolvedModule[], parent: string): void => {
    for (const m of mods) {
      const q = parent ? `${parent}.${m.name}` : m.name;
      collect(q, m.body, m.decl.decorations);
      for (const i of m.interfaces) collect(`${q}.${i.name}`, i.body, i.decl.decorations);
      for (const s of m.surfaces) walkSurface(s, q);
      walk(m.modules, q);
    }
  };
  walk(model.modules, "");
  return { aspectsByQName, fieldsByQName, aspectSet };
}

export function buildDescriptionIndex(
  model: ResolvedModel,
  documents: readonly MarkdownDocument[] = []
): DescriptionIndex {
  const byId = new Map<string, DescriptionDeclRef>();
  const byName = new Map<string, DescriptionDeclRef[]>();
  const { aspectsByQName, fieldsByQName, aspectSet } = collectDocAux(model);
  // Multiple `.md` files can share a name (e.g. `a/runbook.md` + `b/runbook.md`);
  // a `[[runbook#…]]` section ref resolves against the union of their anchors
  // rather than only the last-parsed document.
  const docSections = new Map<string, string[]>();
  for (const d of documents) {
    const existing = docSections.get(d.name);
    const slugs = d.sections.map((s) => s.slug);
    if (existing) existing.push(...slugs);
    else docSections.set(d.name, [...slugs]);
  }
  const spaceOf = (file?: string): QualifiedName | undefined =>
    file ? model.spaces?.get(file) : undefined;

  const push = (ref: DescriptionDeclRef): void => {
    if (ref.id) byId.set(ref.id, ref);
    const append = (key: string): void => {
      const list = byName.get(key);
      if (list) list.push(ref);
      else byName.set(key, [ref]);
    };
    append(ref.name);
    if (ref.qualifiedName !== ref.name) append(ref.qualifiedName);
  };

  const visitModule = (m: ResolvedModule, parentPath: string): void => {
    const qname = parentPath ? `${parentPath}.${m.name}` : m.name;
    push({
      id: m.id,
      name: m.name,
      qualifiedName: qname,
      typeName: m.typeName,
      space: spaceOf(m.decl.source?.file),
    });
    for (const f of m.surfaces) visitSurface(f, qname, spaceOf(m.decl.source?.file));
    for (const i of m.interfaces) visitInterface(i, qname, spaceOf(m.decl.source?.file));
    for (const c of m.modules) visitModule(c, qname);
  };

  const visitSurface = (
    f: ResolvedSurface,
    parentPath: string,
    inherited: QualifiedName | undefined
  ): void => {
    const qname = `${parentPath}.${f.name}`;
    const space = spaceOf(f.decl.source?.file) ?? inherited;
    push({ name: f.name, qualifiedName: qname, typeName: f.typeName, space });
    for (const sub of f.surfaces) visitSurface(sub, qname, space);
    for (const i of f.interfaces) visitInterface(i, qname, space);
  };

  const visitInterface = (
    i: ResolvedInterface,
    parentPath: string,
    inherited: QualifiedName | undefined
  ): void => {
    const qname = `${parentPath}.${i.name}`;
    push({
      name: i.name,
      qualifiedName: qname,
      typeName: i.typeName,
      space: spaceOf(i.decl.source?.file) ?? inherited,
    });
  };

  for (const m of model.modules) visitModule(m, "");
  for (const t of model.types) {
    push({
      id: t.id,
      name: t.name,
      qualifiedName: t.name,
      typeName: t.parentType,
      space: spaceOf(t.source?.file),
    });
  }
  for (const sp of model.subprocesses) {
    const qname = sp.ownerPath && sp.ownerPath.length > 0
      ? `${sp.ownerPath.join(".")}.${sp.name}`
      : sp.name;
    push({
      id: sp.id,
      name: sp.name,
      qualifiedName: qname,
      typeName: "subprocess",
      space: spaceOf(sp.source?.file),
    });
  }
  for (const p of model.processes) {
    const qname = p.ownerPath && p.ownerPath.length > 0
      ? `${p.ownerPath.join(".")}.${p.name}`
      : p.name;
    push({
      id: p.id,
      name: p.name,
      qualifiedName: qname,
      typeName: "process",
      space: spaceOf(p.source?.file),
    });
  }
  for (const v of model.views) {
    const vqname = v.ownerPath && v.ownerPath.length > 0
      ? `${v.ownerPath.join(".")}.${v.name}`
      : v.name;
    push({
      id: v.id,
      name: v.name,
      qualifiedName: vqname,
      typeName: "view",
      space: spaceOf(v.source?.file),
    });
  }

  return {
    resolveId: (id) => byId.get(id),
    resolveName: (name) => byName.get(name) ?? [],
    aspectsFor: (qualifiedName) => aspectsByQName.get(qualifiedName),
    fieldsFor: (qualifiedName) => fieldsByQName.get(qualifiedName),
    resolveAspect: (key, value) =>
      value !== undefined ? aspectSet.has(`${key}:${value}`) : aspectSet.has(key),
    resolveDocument: (name) => docSections.get(name),
    decorations: model.decorationRegistry,
  };
}

// =============================================================================
// Aspect lookup helper
// =============================================================================

/**
 * Build a `getAspect` accessor from an array of effective aspects (e.g.
 * `module.body.aspects` after `applyCascade`). Matches a dotted path
 * against an aspect's qualified-name path.
 */
export function aspectLookup(
  aspects: readonly AspectEntry[]
): (path: readonly string[]) => Value | undefined {
  const map = new Map<string, Value>();
  for (const l of aspects) {
    if (l.drop) continue;
    if (l.value === undefined) continue;
    map.set(l.path.join("."), l.value);
  }
  return (path) => map.get(path.join("."));
}

/**
 * Build a `getField` accessor from an array of effective fields (e.g.
 * `module.body.fields` after `applyCascade`). Matches a dotted path against a
 * field's qualified-name path — the value-axis sibling of `aspectLookup`.
 */
export function fieldLookup(
  fields: readonly FieldDecl[]
): (path: readonly string[]) => Value | undefined {
  const map = new Map<string, Value>();
  for (const f of fields) {
    if (f.value === undefined) continue;
    map.set(f.path.join("."), f.value);
  }
  return (path) => map.get(path.join("."));
}

/**
 * The `@` axis of ONE node: its decorations, then its fields.
 *
 * §4.3.1 puts a declared decoration ahead of a field of the same name in every
 * getter position, so the precedence lives here rather than being restated by
 * each axis — that is how it came to hold in selectors and nowhere else.
 * Returns the bare field lookup when the node carries no decorations, so an
 * undecorated model is unchanged.
 */
export function nodeValueLookup(
  fields: readonly FieldDecl[],
  decorations: readonly DecorationInstance[] | undefined,
  registry: DecorationRegistry | undefined,
): (path: readonly string[]) => Value | undefined {
  const field = fieldLookup(fields);
  const deco = decorationLookup(decorations, registry);
  if (!deco) return field;
  // Fall through ONLY when the name is not a decoration at all. A decoration
  // that is present but has nothing at that argument answers nothing — the
  // shadowed field stays unreachable, as §4.3.1 says it is.
  return (path) => {
    const read = deco(path);
    return read.hit ? read.value : field(path);
  };
}

/**
 * The description context for a node that OWNS the description — both axes
 * composed here rather than by the caller.
 *
 * `makeDescriptionContext` takes two pre-built closures, so a caller can hand
 * it a correct `@@` axis and a half-built `@` one and nothing complains. That
 * is exactly how the LSP hover site kept a bare field lookup after the other
 * three moved, and rendered `@planned.from` as unresolved while the board, the
 * export and the CLI resolved it. Every node-owned description goes through
 * here; `makeDescriptionContext` is for the ownerless ones (a `.md` document,
 * a process), where `@` legitimately resolves to nothing.
 */
export function nodeDescriptionContext(
  index: DescriptionIndex,
  node: {
    readonly aspects: readonly AspectEntry[];
    readonly fields: readonly FieldDecl[];
    readonly decorations?: readonly DecorationInstance[];
  },
  referrerSpace?: QualifiedName
): DescriptionContext {
  return makeDescriptionContext(
    index,
    aspectLookup(node.aspects),
    referrerSpace,
    nodeValueLookup(node.fields, node.decorations, index.decorations)
  );
}

/**
 * Bundle an index + value accessors into a complete `DescriptionContext`. The
 * owning node's `getAspect` (`@@`) and optional `getField` (`@`) back bare
 * interpolation; the index backs the foreign getters `[[X]]@@a` / `[[X]]@f`.
 */
export function makeDescriptionContext(
  index: DescriptionIndex,
  getAspect: (path: readonly string[]) => Value | undefined,
  referrerSpace?: QualifiedName,
  getField: (path: readonly string[]) => Value | undefined = () => undefined
): DescriptionContext {
  return {
    resolveId: (id) => index.resolveId(id),
    resolveName: (name) => index.resolveName(name),
    getAspect,
    getField,
    getForeignAspect: (target, path) => index.aspectsFor(target.qualifiedName)?.(path),
    getForeignField: (target, path) => index.fieldsFor(target.qualifiedName)?.(path),
    resolveAspect: (key, value) => index.resolveAspect(key, value),
    resolveDocument: (name) => index.resolveDocument(name),
    referrerSpace,
  };
}

// =============================================================================
// Helpers
// =============================================================================

function valueToString(v: Value): string {
  switch (v.kind) {
    case "string":
      return v.text;
    case "identifier":
      return v.text;
    case "ref":
      return v.path.join(".");
    case "number":
      return String(v.value);
    case "boolean":
      return v.value ? "true" : "false";
    case "list":
      return v.items.map(valueToString).join(", ");
    case "expr":
      return exprText(v.expr);
  }
}
